import { ChangeEvent, Dispatch, SetStateAction } from "react";
import { CollectorDTO, UtilityBillByIdDTO, DocumentByIdDTO, AllowedValuesDTO, getCollectorById } from "api";
import { useNotify } from "hooks";
import { UtilityBillFormData } from "types";
import { extractErrorMessage } from "utils";

interface UseNotesHandlersParams {
  notes: string;
  setNotes: Dispatch<SetStateAction<string>>;
  setNotesError: Dispatch<SetStateAction<boolean>>;
  setOpenNotesEdit: Dispatch<SetStateAction<boolean>>;
  allowedValues?: AllowedValuesDTO;
  utilityBill?: UtilityBillByIdDTO;
  document?: DocumentByIdDTO;
  newCollectorId?: number;
  collectorsFromSiteData?: CollectorDTO[];
  currentSite: { id: number | undefined; name: string | null };
  defaultValues: UtilityBillFormData;
  notify: ReturnType<typeof useNotify>;
  updateCollectorRequest: (data: any, options?: any) => Promise<any>;
  handleClosePopover: () => void;
}

export const useNotesHandlers = ({
  notes,
  setNotes,
  setNotesError,
  setOpenNotesEdit,
  allowedValues,
  utilityBill,
  document,
  newCollectorId,
  collectorsFromSiteData,
  currentSite,
  defaultValues,
  notify,
  updateCollectorRequest,
  handleClosePopover: closePopover,
}: UseNotesHandlersParams) => {
  const handleEditNotes = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (value.length <= 250) {
      setNotes(value);
      setNotesError(false);
    } else {
      setNotesError(true);
    }
  };

  const handleSaveCollectorNotes = async () => {
    if (!allowedValues || !allowedValues.utilityTypes) {
      notify.error("Unable to update notes: required data is not available");
      return;
    }

    // Get collectorId from newCollectorId, utilityBill, or find it from collectors
    const collectorId = newCollectorId ?? utilityBill?.collectorId;
    if (!collectorId || collectorId === 0) {
      notify.error("Unable to update notes: invalid collector ID");
      return;
    }

    const utilityType = allowedValues.utilityTypes.find(
      (ut: any) => ut.name === defaultValues.utilityTypeName
    );

    if (!utilityType || !utilityType.id) {
      notify.error("Unable to update notes: utility type not found");
      return;
    }

    try {
      const currentCollector: CollectorDTO = await getCollectorById(collectorId);

      if (!currentCollector) {
        notify.error("Unable to update notes: collector not found");
        return;
      }

      const siteId = currentSite.id ?? utilityBill?.siteId ?? document?.siteId ?? 0;

      const noteData = {
        accountNumber: currentCollector.accountNumber,
        allowedDocuments: currentCollector.allowedDocuments,
        frequency: currentCollector.frequency,
        id: currentCollector.id,
        providerName: currentCollector.providerName,
        consumptionUnit: currentCollector.consumptionUnit,
        currency: currentCollector.currency,
        siteId,
        utilityTypeId: utilityType.id,
        notes,
      };

      updateCollectorRequest(noteData, {
        onSuccess: () => {
          notify.success(`Notes successfully Updated!`);
          setOpenNotesEdit(false);
          closePopover();
        },
        onError: (error: unknown) => {
          notify.error(`Unable to update notes: ${extractErrorMessage(error, "Failed to save notes")}`);
        },
      });
    } catch (error: unknown) {
      // This catch handles errors from getCollectorById only
      notify.error(`Unable to update notes: ${extractErrorMessage(error, "Failed to fetch collector data")}`);
    }
  };

  const handleCloseEditNotes = () => {
    // Get notes from the selected collector or utility bill
    const collectorId = newCollectorId ?? utilityBill?.collectorId;
    if (collectorId && collectorsFromSiteData) {
      const selectedCollector = collectorsFromSiteData.find((c: any) => c.id === collectorId);
      setNotes(selectedCollector?.notes || "");
    } else {
      setNotes("");
    }
    setOpenNotesEdit(false);
  };

  const handleOpenEditNotes = async () => {
    setOpenNotesEdit(true);
  };

  return {
    handleEditNotes,
    handleSaveCollectorNotes,
    handleCloseEditNotes,
    handleOpenEditNotes,
  };
};

